import React, { useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Database, AlertCircle, Building2, Search, Info, List, Table } from "lucide-react";
import { useInventoryLiveSearch, ViewMode } from "@/hooks/inventory/useInventoryLiveSearch";
import { InventorySearchResults } from "@/components/inventory/InventorySearchResults";
import { InventoryTableResults } from "@/components/inventory/InventoryTableResults";
import { WarehouseFilter } from "@/components/inventory/WarehouseFilter";
import { RecentSyncBanner } from "@/components/inventory/RecentSyncBanner";
import { Toaster } from "sonner";
import { getMagazijnLabel } from "@/types/inventory";
import { formatDate } from "@/utils/formatDate";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useProductSuggestions } from "@/hooks/useProductSuggestions";

const Inventory = () => {
  const { 
    searchTerm, 
    setSearchTerm, 
    results, 
    isLoading, 
    lastUpdate, 
    sampleProducts, 
    connectionVerified,
    availableWarehouses,
    selectedWarehouse,
    setSelectedWarehouse,
    reloadWarehouses,
    viewMode,
    toggleViewMode
  } = useInventoryLiveSearch();

  const {
    suggestions,
    addSuggestion,
    addProductSuggestions
  } = useProductSuggestions();

  // Add successful search results to suggestions
  useEffect(() => {
    if (results && results.length > 0 && searchTerm && searchTerm.length >= 2) {
      // Add search term when we got results
      addSuggestion(searchTerm);
      
      // Add top results to suggestions
      addProductSuggestions(results.slice(0, 5));
    }
  }, [results, searchTerm]);

  // Log MOQ values for debugging
  useEffect(() => {
    if (results && results.length > 0) {
      console.log('MOQ values in results:', results.map(item => ({
        product: item.product_naam,
        moq: item.moq,
        moqType: typeof item.moq
      })));
    }
  }, [results]);

  // View toggle button icon based on current view mode
  const ViewIcon = viewMode === 'list' ? Table : List;
  const viewButtonLabel = viewMode === 'list' ? 'Tabelweergave' : 'Lijstweergave';

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Voorraad Zoeken</h1>
        <p className="text-muted-foreground">
          Zoek producten op naam, artikelnummer of barcode
        </p>
        {connectionVerified ? (
          <p className="text-green-600 text-sm mt-1 flex items-center gap-1">
            <Database className="h-4 w-4" /> Database verbinding actief
            {availableWarehouses.length > 0 && (
              <span className="ml-1">• {availableWarehouses.length} magazijnen beschikbaar</span>
            )}
          </p>
        ) : (
          <p className="text-amber-600 text-sm mt-1 flex items-center gap-1">
            <AlertCircle className="h-4 w-4" /> Database verbinding controleren...
          </p>
        )}
      </div>

      <RecentSyncBanner />

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Voorraadzoeker</CardTitle>
          {lastUpdate && (
            <div className="text-sm text-muted-foreground flex items-center gap-2">
              🕒 Laatste update: {formatDate(lastUpdate)}
            </div>
          )}
        </CardHeader>
        <CardContent>
          <div className="flex flex-col space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-4">
                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="search"
                    placeholder="Zoek op productnaam, artikelnummer of barcode..."
                    className="pl-8"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    autoFocus
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Slimme zoekfunctie: vind producten met gedeeltelijke woorden
                </p>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  {/* Add warehouse filter indicator when a warehouse is selected */}
                  {selectedWarehouse !== null ? (
                    <div className="bg-blue-50 border border-blue-200 rounded-md px-3 py-2 flex items-center justify-between flex-1 mr-2">
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-blue-500" />
                        <span className="text-sm">
                          Actief filter: <span className="font-bold">{getMagazijnLabel(selectedWarehouse)}</span>
                        </span>
                      </div>
                      <button 
                        className="text-xs text-blue-600 hover:underline"
                        onClick={() => setSelectedWarehouse(null)}
                      >
                        Wissen
                      </button>
                    </div>
                  ) : null}

                  {/* View toggle button */}
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={toggleViewMode}
                    className="flex items-center gap-2"
                  >
                    <ViewIcon className="h-4 w-4" />
                    <span className="hidden sm:inline">{viewButtonLabel}</span>
                  </Button>
                </div>
                
                <div className="flex items-center space-x-2">
                  <div className="flex-1">
                    <WarehouseFilter
                      availableWarehouses={availableWarehouses.map(w => w.id)}
                      selectedWarehouse={selectedWarehouse}
                      onChange={setSelectedWarehouse}
                      onRefresh={reloadWarehouses}
                    />
                  </div>
                </div>
              </div>
            </div>

            {searchTerm.length === 0 && selectedWarehouse === null && 
              sampleProducts.length === 0 && connectionVerified && (
              <div className="p-8 text-center text-amber-600 border border-amber-200 rounded-md bg-amber-50">
                <AlertCircle className="h-6 w-6 mx-auto mb-2" />
                <p>Geen producten gevonden in de database.</p>
                <p className="text-sm mt-1">Controleer of de tabel 'voorraad_live' bestaat en data bevat.</p>
              </div>
            )}

            {/* Show results conditionally based on view mode */}
            {(searchTerm.length > 0 || selectedWarehouse !== null) && (
              <>
                {viewMode === 'list' ? (
                  <InventorySearchResults 
                    results={results}
                    isLoading={isLoading}
                    hasSearched={searchTerm.length > 0 || selectedWarehouse !== null}
                    lastUpdate={lastUpdate}
                  />
                ) : (
                  <InventoryTableResults 
                    results={results}
                    isLoading={isLoading}
                    hasSearched={searchTerm.length > 0 || selectedWarehouse !== null}
                    lastUpdate={lastUpdate}
                  />
                )}
              </>
            )}
          </div>
        </CardContent>
      </Card>
      <Toaster position="top-right" />
    </div>
  );
};

export default Inventory;
